import React, { useState } from "react";
import axios from "axios";
import AuthService from "../service/AmplifyAuth";
import StringUtils from "../utils/StringUtils";
import ErrorUtils from "../utils/ErrorUtils";
import { Button, Card, Field } from "../components/UI";
import { apiHeaders } from "../hooks";
import { useToast } from "../hooks";
import { useNavigate } from "../router";

export default function ChangeEmail() {
  const [emailForm, setEmailForm] = useState({ newEmail: "", verificationCode: "" });
  const [codeSent, setCodeSent] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [isSending, setIsSending] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const addToast = useToast();
  const navigate = useNavigate();

  const update = (field, value) => {
    setEmailForm((form) => ({ ...form, [field]: value }));
    setFieldErrors((errors) => {
      if (!errors[field] || StringUtils.isEmpty(value)) return errors;
      const nextErrors = { ...errors };
      delete nextErrors[field];
      return nextErrors;
    });
  };
  const isBusy = isSending || isConfirming;

  const validateEmail = () => {
    if (StringUtils.isEmpty(emailForm.newEmail)) {
      addToast({ severity: "error", summary: "Validation Error", detail: "New Email is required", life: 5000 });
      setFieldErrors({ newEmail: "New Email is required" });
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailForm.newEmail)) {
      addToast({ severity: "error", summary: "Validation Error", detail: "Please enter a valid email address", life: 5000 });
      setFieldErrors({ newEmail: "Please enter a valid email address" });
      return false;
    }
    setFieldErrors((errors) => {
      if (!errors.newEmail) return errors;
      const nextErrors = { ...errors };
      delete nextErrors.newEmail;
      return nextErrors;
    });
    return true;
  };

  const sendVerificationCode = async () => {
    if (isBusy) return;
    if (!validateEmail()) return;
    try {
      setIsSending(true);
      const data = await AuthService.changeEmail(emailForm.newEmail);
      setCodeSent(true);
      addToast({ severity: "success", summary: "Success", detail: "Verification code sent to new email address", life: 5000 });
      if (data?.verificationCode) {
        update("verificationCode", data.verificationCode);
        addToast({
          severity: "info",
          summary: "Dev Verification Code",
          detail: `Use this code: ${data.verificationCode}`,
          life: 10000,
        });
      }
    } catch (error) {
      addToast({ severity: "error", summary: "Error", detail: ErrorUtils.parseError(error), life: 10000 });
    } finally {
      setIsSending(false);
    }
  };

  const confirmEmail = async () => {
    if (isBusy) return;
    if (StringUtils.isEmpty(emailForm.verificationCode)) {
      addToast({ severity: "error", summary: "Validation Error", detail: "Verification code is required", life: 5000 });
      setFieldErrors({ verificationCode: "Verification code is required" });
      return;
    }
    try {
      setIsConfirming(true);
      const user = await AuthService.currentAuthenticatedUserAttributes();
      await AuthService.confirmEmailChange(emailForm.verificationCode);
      await axios.patch(`/v1/users/${user.username.toLowerCase()}/${emailForm.newEmail.toLowerCase()}`, {}, { headers: apiHeaders });
      addToast({ severity: "success", summary: "Success", detail: "Email Update Successful", life: 5000 });
      navigate("/account");
    } catch (error) {
      addToast({ severity: "error", summary: "Error", detail: ErrorUtils.parseError(error), life: 10000 });
    } finally {
      setIsConfirming(false);
    }
  };

  const onEnter = (handler) => (event) => {
    if (event.key === "Enter") {
      handler();
    }
  };

  return (
    <div className="account-page-modern account-form-page">
      <Card className="account-form-card">
        <div className="account-form-head">
          <p className="dashboard-section-kicker">Security</p>
          <h2>{codeSent ? "Verify your new email" : "Update Email"}</h2>
        </div>
        {!codeSent ? (
          <Field label="New email" required error={fieldErrors.newEmail}>
            <input type="email" className="form-control" placeholder="New email" value={emailForm.newEmail} autoComplete="email" autoCapitalize="none" spellCheck="false" onKeyDown={onEnter(sendVerificationCode)} onChange={(event) => update("newEmail", event.target.value)} />
          </Field>
        ) : (
          <Field label="Verification code" required error={fieldErrors.verificationCode}>
            <input className="form-control" placeholder="Enter verification code" value={emailForm.verificationCode} autoComplete="one-time-code" inputMode="numeric" onKeyDown={onEnter(confirmEmail)} onChange={(event) => update("verificationCode", event.target.value)} />
          </Field>
        )}
        {codeSent ? <p className="account-form-note">We sent a 6-digit verification code to {emailForm.newEmail}. Enter the code to continue.</p> : null}
        {codeSent ? <p className="account-form-help">Did not receive the code? Please wait up to 10 minutes and check your spam or junk folder.</p> : null}
        <div className="account-form-actions">
          <Button label="Cancel" className="btn-danger" disabled={isBusy} onClick={() => navigate("/account")} />
          {codeSent ? <Button label={isBusy ? "Sending..." : "Resend Code"} className="btn-secondary" disabled={isBusy} onClick={sendVerificationCode} /> : null}
          {codeSent ? <Button label={isBusy ? "Confirming..." : "Confirm"} disabled={isBusy} onClick={confirmEmail} /> : null}
          {!codeSent ? <Button label={isBusy ? "Sending..." : "Send Code"} disabled={isBusy} onClick={sendVerificationCode} /> : null}
        </div>
      </Card>
    </div>
  );
}
